import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { getAPIPayload } from "../../../apis/getApi";
import { postAPIPayload } from "../../../apis/postApi";
import {
  arrayBufferToBase64,
  getObjLength,
  isArrayBufferLike,
  isPdfArrayBuffer,
  parseArrayBufferToJson,
  redirectToLoginForInvalidSession,
} from "../../../components/helpers/utils";
import {
  parsePropertyDetailCacheContext,
  writePersistedPropertyDetail,
} from "../../../components/common/userPropertiesSections/propertyIntelCache";
import { propertiesAction } from "../../slices/properties/propertiesSlice";

const isPropertyFlowOpen = (state) =>
  Object.keys(state?.properties?.propertyFlowOpenSources || {}).length > 0;

// Customer list POST (customer/properties-v1[/parentId]): reset the Add Property
// / Add Subscription / Upgrade Subscription state. If one of those modals is
// still open (e.g. list reload before the Security PIN step) defer the reset
// until it closes so nothing in progress is touched. Other portals share
// fetchPropertyList with their own URLs and are skipped.
function* resetPropertyFlowOnCustomerListLoad(payload) {
  if (!String(payload?.url || "").startsWith("customer/properties-v1")) {
    return;
  }
  if (yield select(isPropertyFlowOpen)) {
    yield put(propertiesAction.markPendingPropertyFlowReset());
    return;
  }
  yield put(propertiesAction.resetPropertyFlowState());
}

// Last flow modal closed: run the reset a list POST deferred while it was open.
export function* propertyFlowClosedSaga({ payload }) {
  if (payload?.open) {
    return;
  }
  const pending = yield select(
    (state) => state?.properties?.pendingPropertyFlowReset,
  );
  if (pending && !(yield select(isPropertyFlowOpen))) {
    yield put(propertiesAction.resetPropertyFlowState());
  }
}

export function* fetchPropertyListSaga({ payload }) {
  console.log("in sagas--", payload);
  yield* resetPropertyFlowOnCustomerListLoad(payload);
  try {
    const response = yield call(postAPIPayload, payload);
    if (response?.success === false || !Array.isArray(response?.data)) {
      console.error("fetchPropertyListSaga failed", response?.message || response);
      if (
        redirectToLoginForInvalidSession(
          response?.message,
          response?.statuscode,
          response,
        )
      ) {
        return;
      }
      yield put(
        propertiesAction.setPropertyList({
          data: [],
          count: 0,
          success: false,
          message: response?.message || "Unable to load properties.",
        }),
      );
      return;
    }
    yield put(propertiesAction.setPropertyList(response));
  } catch (error) {
    console.error("fetchPropertyListSaga failed", error);
    yield put(
      propertiesAction.setPropertyList({
        data: [],
        count: 0,
        success: false,
        message: error?.message || "Unable to load properties.",
      }),
    );
  }
}

export function* fetchUserPropertySaga({ payload }) {
  console.log("in sagas--", payload);
  try {
    if (!getObjLength(payload)) {
      yield put(propertiesAction.setUserPropertyData({}));
      return;
    }
    // The search endpoint is public; when signed in, send the token anyway so the
    // admin log can record who searched and how (address or parcel number).
    const signedIn = Boolean(localStorage.getItem("authToken"));
    const response = yield call(
      postAPIPayload,
      signedIn ? { ...payload, token: true } : payload,
    );
    console.log("user Property response==>.", response);
    yield put(
      propertiesAction.setUserPropertyData(
        response || {
          success: false,
          message: "Unable to search properties.",
          data: { message: "Unable to search properties." },
        },
      ),
    );
  } catch (error) {
    console.error("fetchUserPropertySaga failed", error);
    yield put(
      propertiesAction.setUserPropertyData({
        success: false,
        message: error?.message || "Unable to search properties.",
        data: { message: error?.message || "Unable to search properties." },
      }),
    );
  }
}

export function* getgenerateQuestionsSaga({ payload }) {
  console.log("in sagas--", payload);
  try {
    const response = yield call(postAPIPayload, payload);
    console.log("user Property response==>.", response);
    yield put(propertiesAction.setGeneratedQuestionsData(response));
  } catch (error) {
    console.error("getgenerateQuestionsSaga failed", error);
    yield put(
      propertiesAction.setGeneratedQuestionsData({
        success: false,
        data: { message: "Unable to load security questions." },
      }),
    );
  }
}

export function* getVerifyAnswersSaga({ payload }) {
  console.log("in sagas--", payload);
  const response = yield call(postAPIPayload, payload);
  console.log("user Property response==>.", response);
  yield put(propertiesAction.setVerifedAnswersData(response));
}

export function* getDocumetTypesSaga({ payload }) {
  console.log("in sagas--", payload);
  const response = yield call(getAPIPayload, payload);
  console.log("DocumetType response==>.", response);
  yield put(propertiesAction.fetchDocumentTypeList(response));
}

export function* getReqDocumentSaga({ payload }) {
  console.log("in sagas--", payload);
  const response = yield call(postAPIPayload, payload);
  console.log("fethRequestedDoc response==>.", response);
  yield put(propertiesAction.fethRequestedDoc(response));
}

export function* getGeneratedDocSaga({ payload }) {
  console.log("in sagas--", payload);
  const response = yield call(postAPIPayload, payload);
  console.log("fethGeneratedDoc response==>.", response);
  yield put(propertiesAction.fethGeneratedDoc(response));
}

export function* getPropertyPaymentSaga({ payload }) {
  console.log("in sagas--", payload);
  const response = yield call(postAPIPayload, payload);
  console.log("fethGeneratedDoc response==>.", response);
  yield put(propertiesAction.fethPayment(response));
}

export function* cancelSubscriptionSaga({ payload }) {
  const response = yield call(postAPIPayload, payload);
  yield put(propertiesAction.setCancelSubscription(response));
}

export function* fetchSubscriptionHistorySaga({ payload }) {
  console.log("subscripion saga payload--", payload);
  const response = yield call(postAPIPayload, payload);
  console.log("subscripion saga response==>.", response);
  yield put(propertiesAction.setSubscriptionHistory(response));
}

export function* addPropertySubscriptionSaga({ payload }) {
  const response = yield call(postAPIPayload, payload);
  yield put(propertiesAction.setAddPropertySubscription(response));
}

export function* editPropertiesDetailsSaga({ payload }) {
  const response = yield call(getAPIPayload, payload);
  yield put(propertiesAction.setEditPropertiesData(response));
}

export function* securityPropertiesQueDetailsSaga({ payload }) {
  const response = yield call(postAPIPayload, payload);
  yield put(propertiesAction.setSecurityPropertiesQueData(response));
}

export function* verifySecurityInfoDetailsSaga({ payload }) {
  const response = yield call(postAPIPayload, payload);
  yield put(propertiesAction.fetchVerifySecurityInfoData(response));
}

export function* stopSubscriptionSaga({ payload }) {
  const response = yield call(getAPIPayload, payload);
  yield put(propertiesAction.fetchStopSubscriptionDetails(response));
}

export function* getViewPropertyDetailsSaga({ payload }) {
  try {
    const response = yield call(postAPIPayload, payload);
    yield put(propertiesAction.setViewPropertyDetails(response));

    if (response?.success) {
      const { apiPrefix, detailId } = parsePropertyDetailCacheContext(
        payload?.url,
      );
      if (detailId) {
        writePersistedPropertyDetail(apiPrefix, detailId, response);
      }
    }
  } catch (error) {
    console.error("getViewPropertyDetailsSaga failed", error);
    yield put(
      propertiesAction.setViewPropertyDetails({
        success: false,
        data: { message: "Unable to load property details." },
      }),
    );
  }
}

export function* getBasicMonitoringDetailsSaga({ payload }) {
  const response = yield call(postAPIPayload, payload);
  yield put(propertiesAction.fetchBasicMonitoringDetails(response));
}

const parseTtbDocumentArrayBuffer = (buffer) => {
  if (isPdfArrayBuffer(buffer)) {
    const base64Pdf = arrayBufferToBase64(buffer);
    if (!base64Pdf) {
      return {
        success: false,
        error: "Unable to read document.",
      };
    }
    return { success: true, data: base64Pdf };
  }

  const parsed = parseArrayBufferToJson(buffer);
  if (parsed && typeof parsed === "object") {
    return parsed;
  }

  return {
    success: false,
    error: "Unable to read document.",
  };
};

export function* fetchTtbDocumentSaga({ payload }) {
  const response = yield call(postAPIPayload, {
    ...payload,
    responseType: "arraybuffer",
  });

  if (isArrayBufferLike(response)) {
    yield put(
      propertiesAction.setTtbDocument(parseTtbDocumentArrayBuffer(response)),
    );
    return;
  }

  yield put(propertiesAction.setTtbDocument(response));
}

export function* watchPropertiesSagaAsync() {
  yield takeLatest(propertiesAction.fetchPropertyList, fetchPropertyListSaga);
  yield takeEvery(propertiesAction.setPropertyFlowOpen, propertyFlowClosedSaga);
  yield takeLatest(propertiesAction.fetchUserProperty, fetchUserPropertySaga);
  // takeLatest prevents stacked add-property-in-report / generate-questions
  // calls when the select-property effect re-runs (e.g. statesCodeList load).
  yield takeLatest(propertiesAction.generateQuestions, getgenerateQuestionsSaga);
  yield takeEvery(propertiesAction.verifyAnswers, getVerifyAnswersSaga);
  yield takeEvery(propertiesAction.getDocumentTypes, getDocumetTypesSaga);
  yield takeEvery(propertiesAction.reqDocument, getReqDocumentSaga);
  yield takeEvery(propertiesAction.generateDocument, getGeneratedDocSaga);
  yield takeEvery(propertiesAction.paymentForProperty, getPropertyPaymentSaga);
  yield takeEvery(propertiesAction.cancelSubscription, cancelSubscriptionSaga);
  yield takeEvery(
    propertiesAction.fetchSubscriptionHistory,
    fetchSubscriptionHistorySaga,
  );
  yield takeEvery(
    propertiesAction.addPropertySubscription,
    addPropertySubscriptionSaga,
  );
  yield takeEvery(
    propertiesAction.editPropertiesDetails,
    editPropertiesDetailsSaga,
  );
  yield takeEvery(
    propertiesAction.securityPropertiesQueDetails,
    securityPropertiesQueDetailsSaga,
  );
  yield takeEvery(
    propertiesAction.verifySecurityInfoDetails,
    verifySecurityInfoDetailsSaga,
  );
  yield takeEvery(
    propertiesAction.setStopSubscriptionData,
    stopSubscriptionSaga,
  );
  yield takeEvery(
    propertiesAction.getViewPropertyDetails,
    getViewPropertyDetailsSaga,
  );
  yield takeEvery(
    propertiesAction.getBasicMonitoringData,
    getBasicMonitoringDetailsSaga,
  );
  yield takeEvery(propertiesAction.fetchTtbDocument, fetchTtbDocumentSaga);
}
